import { useState, useEffect, useCallback } from"react";
import { FileSearch, Shield, TrendingUp, FlaskConical, Microscope, ChevronRight, BarChart3, Check, X, Minus, ArrowUpRight, BookOpen, Beaker } from"lucide-react";
import { Badge } from"@/components/ui/badge";

// ─── Mock data for previews (anonymised, no confidential info) ────────────────

const TECHNICAL_DD_SLIDES = [
 {
 title:"Technology Stack Analysis",
 content: (
 <div className="space-y-3">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Competitive Positioning</div>
 {[
 { label:"Platform Defensibility", score: 88, color:"bg-primary" },
 { label:"IP Moat Strength", score: 74, color:"bg-primary" },
 { label:"Tech Differentiation", score: 91, color:"bg-primary" },
 { label:"Scalability Index", score: 82, color:"bg-primary" },
 ].map((item) => (
 <div key={item.label} className="space-y-1">
 <div className="flex justify-between text-[10px]">
 <span className="text-foreground font-medium">{item.label}</span>
 <span className="text-primary font-bold">{item.score}/100</span>
 </div>
 <div className="h-1.5 rounded-full bg-muted overflow-hidden">
 <div className={`h-full ${item.color} rounded-full transition-all duration-700`} style={{ width: `${item.score}%` }} />
 </div>
 </div>
 ))}
 </div>
 ),
 },
 {
 title:"Competitor Landscape",
 content: (
 <div className="space-y-2">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Market Players</div>
 {[
 { name:"Target Company", type:"Private", stage:"Series B", moat:"High", badge:"bg-primary/10 text-primary" },
 { name:"Competitor A", type:"Public", stage:"Listed", moat:"Medium", badge:"bg-muted text-muted-foreground" },
 { name:"Competitor B", type:"Private", stage:"Series C", moat:"Low", badge:"bg-muted text-muted-foreground" },
 { name:"Competitor C", type:"Private", stage:"Seed", moat:"Emerging", badge:"bg-muted text-muted-foreground" },
 ].map((c) => (
 <div key={c.name} className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 ${c.badge.includes("primary") ?"bg-primary/5 border border-primary/20" :"bg-muted"}`}>
 <div className="flex items-center gap-2">
 <span className="text-[10px] font-semibold text-foreground">{c.name}</span>
 <span className={`text-[9px] rounded px-1 py-0.5 font-medium ${c.badge}`}>{c.type}</span>
 </div>
 <div className="flex items-center gap-2">
 <span className="text-[9px] text-muted-foreground">{c.stage}</span>
 <span className="text-[9px] text-primary font-medium">{c.moat} moat</span>
 </div>
 </div>
 ))}
 </div>
 ),
 },
 {
 title:"Technology Readiness",
 content: (
 <div className="space-y-2">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">TRL Assessment</div>
 {[
 { phase:"Core Platform", trl:"TRL 7", status:"Validated", ok: true },
 { phase:"Neuromorphic Chip Integration", trl:"TRL 6", status:"Demonstrated", ok: true },
 { phase:"Edge Deployment", trl:"TRL 5", status:"In Progress", ok: true },
 { phase:"Full-Scale Production", trl:"TRL 4", status:"Development", ok: false },
 ].map((item) => (
 <div key={item.phase} className="flex items-center justify-between rounded-lg bg-muted px-2.5 py-1.5">
 <div>
 <p className="text-[10px] font-medium text-foreground">{item.phase}</p>
 <p className="text-[9px] text-muted-foreground">{item.status}</p>
 </div>
 <span className={`text-[9px] font-bold rounded px-1.5 py-0.5 ${item.ok ?"bg-primary/10 text-primary" :"bg-muted text-muted-foreground"}`}>{item.trl}</span>
 </div>
 ))}
 </div>
 ),
 },
];

const IP_PATENT_SLIDES = [
 {
 title:"Patent Landscape Overview",
 content: (
 <div className="space-y-3">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Filing Activity by Region</div>
 {[
 { region:"United States (USPTO)", count: 47, pct: 85 },
 { region:"European Patent Office", count: 31, pct: 56 },
 { region:"China (CNIPA)", count: 24, pct: 44 },
 { region:"Japan (JPO)", count: 12, pct: 22 },
 ].map((r) => (
 <div key={r.region} className="space-y-1">
 <div className="flex justify-between text-[10px]">
 <span className="text-foreground">{r.region}</span>
 <span className="text-primary font-bold">{r.count} patents</span>
 </div>
 <div className="h-1.5 rounded-full bg-muted overflow-hidden">
 <div className="h-full bg-primary rounded-full" style={{ width: `${r.pct}%` }} />
 </div>
 </div>
 ))}
 <div className="mt-3 rounded-lg bg-primary/5 border border-primary/20 p-2">
 <p className="text-[9px] text-primary font-semibold">💡 Whitespace Opportunity: Neuromorphic low-power edge inference — minimal prior art detected</p>
 </div>
 </div>
 ),
 },
 {
 title:"Key Patent Holdings",
 content: (
 <div className="space-y-2">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Top Assignees</div>
 {[
 { name:"Target Co.", patents: 47, trend:"+12 YoY", highlight: true },
 { name:"University Research Lab A", patents: 28, trend:"+4 YoY", highlight: false },
 { name:"Tech Corp B", patents: 19, trend:"+7 YoY", highlight: false },
 { name:"Startup C", patents: 11, trend:"+11 YoY", highlight: false },
 ].map((a) => (
 <div key={a.name} className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 ${a.highlight ?"bg-primary/5 border border-primary/20" :"bg-muted"}`}>
 <span className="text-[10px] font-medium text-foreground">{a.name}</span>
 <div className="flex items-center gap-2">
 <span className="text-[10px] font-bold text-foreground">{a.patents}</span>
 <span className="text-[9px] text-primary">{a.trend}</span>
 </div>
 </div>
 ))}
 </div>
 ),
 },
];

const FINANCIAL_SLIDES = [
 {
 title:"Funding History",
 content: (
 <div className="space-y-2">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Investment Rounds</div>
 {[
 { round:"Seed", amount:"$2.1M", date:"2021", investors:"3 Angels", color:"bg-muted text-muted-foreground" },
 { round:"Series A", amount:"$14.5M", date:"2022", investors:"Lead VC + 2", color:"bg-primary/10 text-primary" },
 { round:"Series B", amount:"$48.0M", date:"2024", investors:"Strategic + VC", color:"bg-primary/20 text-primary" },
 ].map((r) => (
 <div key={r.round} className="flex items-center justify-between rounded-lg bg-muted px-2.5 py-1.5">
 <div>
 <p className="text-[10px] font-bold text-foreground">{r.round}</p>
 <p className="text-[9px] text-muted-foreground">{r.investors} · {r.date}</p>
 </div>
 <span className={`text-[11px] font-bold rounded px-1.5 py-0.5 ${r.color}`}>{r.amount}</span>
 </div>
 ))}
 <div className="rounded-lg bg-muted px-2.5 py-1.5 mt-1">
 <div className="flex justify-between">
 <span className="text-[10px] text-muted-foreground">Total Raised</span>
 <span className="text-[11px] font-bold text-primary">$64.6M</span>
 </div>
 <div className="flex justify-between mt-0.5">
 <span className="text-[10px] text-muted-foreground">Last Valuation</span>
 <span className="text-[10px] font-semibold text-foreground">~$310M</span>
 </div>
 </div>
 </div>
 ),
 },
 {
 title:"Investor Sentiment",
 content: (
 <div className="space-y-3">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Signal Analysis</div>
 {[
 { signal:"VC Sentiment", value:"Bullish", score: 82, positive: true },
 { signal:"Market Timing", value:"Favourable", score: 76, positive: true },
 { signal:"Burn Efficiency", value:"Moderate", score: 58, positive: true },
 { signal:"Exit Readiness", value:"24–36 mo", score: 45, positive: false },
 ].map((s) => (
 <div key={s.signal} className="space-y-1">
 <div className="flex justify-between text-[10px]">
 <span className="text-foreground">{s.signal}</span>
 <span className={`font-medium ${s.positive ?"text-primary" :"text-muted-foreground"}`}>{s.value}</span>
 </div>
 <div className="h-1.5 rounded-full bg-muted overflow-hidden">
 <div className="h-full bg-primary rounded-full" style={{ width: `${s.score}%` }} />
 </div>
 </div>
 ))}
 </div>
 ),
 },
];

const SCIENTIFIC_SLIDES = [
 {
 title:"PubMed Publications",
 content: (
 <div className="space-y-2">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Recent High-Impact Papers</div>
 {[
 { title:"Ex vivo gene correction via AAV9 in dystrophic muscle models", journal:"Nature Medicine", year: 2024, citations: 142, impact:"High" },
 { title:"Lentiviral haematopoietic stem cell therapy: 5-year follow-up", journal:"NEJM", year: 2024, citations: 89, impact:"High" },
 { title:"Off-target editing risks in CRISPR-Cas9 delivery systems", journal:"Science", year: 2023, citations: 217, impact:"High" },
 ].map((p) => (
 <div key={p.title} className="rounded-lg bg-muted px-2.5 py-1.5">
 <p className="text-[9px] font-medium text-foreground leading-tight line-clamp-2">{p.title}</p>
 <div className="flex items-center gap-2 mt-1">
 <span className="text-[9px] text-primary font-semibold">{p.journal}</span>
 <span className="text-[9px] text-muted-foreground">{p.year}</span>
 <span className="text-[9px] text-muted-foreground">{p.citations} citations</span>
 </div>
 </div>
 ))}
 </div>
 ),
 },
 {
 title:"Key Opinion Leaders",
 content: (
 <div className="space-y-2">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">KOL Influence Mapping</div>
 {[
 { name:"Prof. A. Morrison", institution:"Harvard Medical School", hIndex: 68, field:"Gene Therapy" },
 { name:"Dr. L. Chen", institution:"Stanford Genomics Lab", hIndex: 54, field:"CRISPR" },
 { name:"Prof. R. Patel", institution:"UCL Great Ormond St.", hIndex: 47, field:"Rare Diseases" },
 ].map((kol) => (
 <div key={kol.name} className="flex items-center justify-between rounded-lg bg-muted px-2.5 py-1.5">
 <div>
 <p className="text-[10px] font-semibold text-foreground">{kol.name}</p>
 <p className="text-[9px] text-muted-foreground">{kol.institution}</p>
 </div>
 <div className="text-right">
 <p className="text-[10px] font-bold text-primary">h={kol.hIndex}</p>
 <p className="text-[9px] text-muted-foreground">{kol.field}</p>
 </div>
 </div>
 ))}
 </div>
 ),
 },
];

const CLINICAL_TRIALS_SLIDES = [
 {
 title:"Pipeline Overview",
 content: (
 <div className="space-y-2">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Active Clinical Trials Pipeline</div>
 {[
 { phase:"Preclinical", count: 8, color:"bg-muted-foreground/60", width: 100 },
 { phase:"Phase I", count: 5, color:"bg-primary/40", width: 62 },
 { phase:"Phase II", count: 3, color:"bg-primary/70", width: 37 },
 { phase:"Phase III", count: 1, color:"bg-primary", width: 12 },
 { phase:"Approved", count: 1, color:"bg-primary", width: 12 },
 ].map((p) => (
 <div key={p.phase} className="flex items-center gap-2">
 <span className="text-[9px] text-muted-foreground w-16 shrink-0">{p.phase}</span>
 <div className="flex-1 h-4 rounded-sm bg-muted overflow-hidden">
 <div className={`h-full ${p.color} rounded-sm flex items-center pl-1.5`} style={{ width: `${p.width}%` }}>
 <span className="text-[8px] text-primary-foreground font-bold">{p.count}</span>
 </div>
 </div>
 </div>
 ))}
 </div>
 ),
 },
 {
 title:"Trial Details",
 content: (
 <div className="space-y-2">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Selected Active Trials</div>
 {[
 { nct:"NCT0531xxxx", phase:"Phase III", condition:"Beta-thalassaemia", status:"Recruiting", enrol: 180 },
 { nct:"NCT0498xxxx", phase:"Phase II", condition:"Haemophilia B", status:"Active", enrol: 64 },
 { nct:"NCT0612xxxx", phase:"Phase II", condition:"Sickle Cell Disease", status:"Recruiting", enrol: 95 },
 ].map((t) => (
 <div key={t.nct} className="rounded-lg bg-muted px-2.5 py-1.5">
 <div className="flex items-center justify-between">
 <span className="text-[9px] font-mono text-muted-foreground">{t.nct}</span>
 <span className={`text-[8px] font-semibold rounded px-1 py-0.5 ${t.status ==="Recruiting" ?"bg-primary/10 text-primary" :"bg-muted text-muted-foreground"}`}>{t.status}</span>
 </div>
 <p className="text-[10px] font-medium text-foreground mt-0.5">{t.condition}</p>
 <div className="flex gap-2 mt-0.5">
 <span className="text-[9px] text-primary font-medium">{t.phase}</span>
 <span className="text-[9px] text-muted-foreground">n={t.enrol}</span>
 </div>
 </div>
 ))}
 </div>
 ),
 },
 {
 title:"Regulatory Milestones",
 content: (
 <div className="space-y-2">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">FDA / EMA Pathway</div>
 {[
 { milestone:"Orphan Drug Designation (FDA)", date:"Mar 2022", done: true },
 { milestone:"Breakthrough Therapy Designation", date:"Nov 2022", done: true },
 { milestone:"Phase III Interim Analysis", date:"Q2 2025", done: false },
 { milestone:"BLA Submission Target", date:"Q4 2025", done: false },
 { milestone:"PDUFA Date (est.)", date:"Q3 2026", done: false },
 ].map((m) => (
 <div key={m.milestone} className="flex items-center gap-2">
 <div className={`h-3.5 w-3.5 rounded-full flex items-center justify-center shrink-0 ${m.done ?"bg-primary" :"bg-muted border border-border"}`}>
 {m.done && <Check className="h-2 w-2 text-primary-foreground" />}
 </div>
 <div className="flex-1 flex items-center justify-between">
 <p className={`text-[9px] ${m.done ?"text-foreground font-medium" :"text-muted-foreground"}`}>{m.milestone}</p>
 <span className="text-[8px] text-muted-foreground shrink-0 ml-1">{m.date}</span>
 </div>
 </div>
 ))}
 </div>
 ),
 },
];

// ─── Feature definitions ──────────────────────────────────────────────────────

const FEATURES = [
 {
 id:"technical-dd",
 icon: <FileSearch className="h-6 w-6" />,
 title:"Technical DD",
 description:"Technology, market positioning & competitive moat analysis",
 slides: TECHNICAL_DD_SLIDES,
 badge:"Core Analysis",
 },
 {
 id:"ip-patents",
 icon: <Shield className="h-6 w-6" />,
 title:"IP & Patents",
 description:"Patent landscape mapping, filing trends & whitespace",
 slides: IP_PATENT_SLIDES,
 badge:"IP Intelligence",
 },
 {
 id:"financial",
 icon: <TrendingUp className="h-6 w-6" />,
 title:"Financial Signals",
 description:"Funding history, valuations & investor sentiment",
 slides: FINANCIAL_SLIDES,
 badge:"Financial Analysis",
 },
 {
 id:"scientific",
 icon: <BookOpen className="h-6 w-6" />,
 title:"Scientific Research",
 description:"PubMed publications, clinical trials & KOL analysis",
 slides: SCIENTIFIC_SLIDES,
 badge:"Scientific Research",
 },
 {
 id:"clinical-trials",
 icon: <Beaker className="h-6 w-6" />,
 title:"Clinical Trials",
 description:"Pipeline visualisation, trial status & regulatory pathways",
 slides: CLINICAL_TRIALS_SLIDES,
 badge:"ClinicalTrials.gov",
 },
];

// ─── Preview Panel ────────────────────────────────────────────────────────────

function PreviewPanel({ feature, slideIndex }: { feature: typeof FEATURES[0]; slideIndex: number }) {
 // Clamp index to valid range — guards against the brief render cycle
 // where slideIndex hasn't reset yet after switching features
 const safeIndex = Math.min(slideIndex, feature.slides.length - 1);
 const slide = feature.slides[safeIndex];
 return (
 <div className="h-full flex flex-col">
 {/* Header bar */}
 <div className="flex items-center justify-between border-b border-border px-3 py-2 bg-muted">
 <div className="flex items-center gap-2">
 <div className="h-2 w-2 rounded-full bg-destructive/60" />
 <div className="h-2 w-2 rounded-full bg-yellow-400/60" />
 <div className="h-2 w-2 rounded-full bg-primary/60" />
 </div>
 <div className="flex items-center gap-1.5">
 <span className="text-[9px] font-medium text-muted-foreground">Point Science Dashboard</span>
 <Badge variant="secondary" className="text-[8px] px-1.5 py-0 h-4">{feature.badge}</Badge>
 </div>
 <div className="w-12" />
 </div>

 {/* Slide content */}
 <div className="flex-1 overflow-hidden p-3">
 <p className="text-[10px] font-bold text-foreground mb-2.5">{slide.title}</p>
 <div className="text-[10px]">{slide.content}</div>
 </div>

 {/* Slide dots */}
 <div className="flex items-center justify-center gap-1.5 pb-2">
 {feature.slides.map((_, i) => (
 <div
 key={i}
 className={`rounded-full transition-all duration-300 ${i === slideIndex ?"w-4 h-1.5 bg-primary" :"w-1.5 h-1.5 bg-border"}`}
 />
 ))}
 </div>
 </div>
 );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const FeatureShowcase = () => {
 const [activeFeature, setActiveFeature] = useState<string | null>(null);
 const [slideIndex, setSlideIndex] = useState(0);
 const [isPaused, setIsPaused] = useState(false);

 const active = FEATURES.find((f) => f.id === activeFeature) ?? null;

 // Auto-advance slides
 useEffect(() => {
 if (!active || isPaused) return;
 const total = active.slides.length;
 const timer = setInterval(() => {
 setSlideIndex((i) => (i + 1) % total);
 }, 2800);
 return () => clearInterval(timer);
 }, [active, isPaused]);

 // Reset slide when feature changes
 useEffect(() => {
 setSlideIndex(0);
 }, [activeFeature]);

 const handleFeatureEnter = useCallback((id: string) => {
 setActiveFeature(id);
 setIsPaused(false);
 }, []);

 return (
 <div className="mx-auto mt-14 max-w-5xl">
 {/* Feature cards */}
 <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
 {FEATURES.map((feature) => {
 const isActive = activeFeature === feature.id;
 return (
 <button
 key={feature.id}
 className={`group rounded-xl border p-5 text-left transition-all duration-300 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
 isActive
 ?"border-primary/50 bg-primary/5 shadow-md"
 :"border-border bg-card hover:border-primary/30 hover:bg-card"
 }`}
 style={{ boxShadow: isActive ?"0 0 0 1px hsl(var(--primary)/0.15), 0 4px 20px hsl(var(--primary)/0.12)" :"var(--shadow-card)" }}
 onMouseEnter={() => handleFeatureEnter(feature.id)}
 onClick={() => handleFeatureEnter(feature.id)}
 aria-pressed={isActive}
 >
 <div className={`mb-3 inline-flex rounded-lg p-3 transition-colors ${isActive ?"bg-primary text-primary-foreground" :"bg-primary/10 text-primary"}`}>
 {feature.icon}
 </div>
 <h2 className="mb-1.5 font-semibold text-sm text-foreground">{feature.title}</h2>
 <p className="text-xs text-muted-foreground leading-relaxed">{feature.description}</p>
 {isActive && (
 <div className="mt-2 flex items-center gap-1 text-[10px] text-primary font-medium">
 <span>Live preview</span>
 <ChevronRight className="h-2.5 w-2.5" />
 </div>
 )}
 </button>
 );
 })}
 </div>

 {/* Preview panel - appears below cards when a feature is active */}
 <div
 className={`mt-4 overflow-hidden rounded-xl border border-border bg-card transition-all duration-500 ${
 active ?"max-h-[340px] opacity-100" :"max-h-0 opacity-0 border-transparent"
 }`}
 style={{ boxShadow: active ?"var(--shadow-card)" :"none" }}
 onMouseEnter={() => setIsPaused(true)}
 onMouseLeave={() => setIsPaused(false)}
 >
 {active && (
 <div className="h-[300px]">
 <PreviewPanel feature={active} slideIndex={slideIndex} />
 </div>
 )}
 </div>

 {/* Hint text */}
 {!active && (
 <p className="mt-5 text-center text-xs text-muted-foreground animate-pulse">
 Hover over a feature to preview the dashboard
 </p>
 )}
 </div>
 );
};
